import { useMemo, useState } from "react";
import {
  MessageCircle,
  Ban,
  AlertTriangle,
  Trash2,
  BadgeCheck,
} from "lucide-react";
import { toast } from "@/state/hooks";
import { Card, CardContent } from "@/ui/components/ui/card";
import { ListContainer } from "@/ui/components/shared/ListItem";
import { UserProfilePicture } from "@/ui/components/UserProfilePicture";
import { useActions, useAppState } from "@/state/legacy_module_api";
import { Contact as User, Group } from "@/data/schemas";
import { createDirectThreadKey } from "@/core_bridge/thread_keys";
import { useStore } from "@/state/legacy_module_api";
import { threadService, db } from "@/services/module_api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/ui/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/ui/select";
import { Textarea } from "@/ui/components/ui/textarea";
import { Button } from "@/ui/components/ui/button";
import { Label } from "@/ui/components/ui/label";

type Props = {
  userId?: string;
};

/**
 * Inline user profile panel for the right-hand pane.
 * Mirrors the popup actions but lives in-page.
 */
const UserProfilePane = ({ userId }: Props) => {
  const { contactsById } = useAppState().user;
  const { myUserId } = useAppState();
  const actions = useActions().user;
  const setActiveTab = useStore.getState().setActiveTab;
  const selectThread = useStore.getState().selectThread;
  const setMessages = (useStore.getState() as any).setMessages;
  const setHasMore = (useStore.getState() as any).setHasMore;

  // Report dialog state
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [reportCategory, setReportCategory] = useState<string>("other");
  const [reportDescription, setReportDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const user = useMemo<User | undefined>(() => {
    if (!userId) return undefined;
    return contactsById[userId] as User | undefined;
  }, [userId, contactsById]);

  if (!userId) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground px-6">
        Select a user to view their profile.
      </div>
    );
  }

  if (!user) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground px-6">
        No details available for this user.
      </div>
    );
  }

  const isMyself = user.userId === myUserId;

  const displayName = isMyself
    ? "My Self"
    : user.fullHandle || user.username || user.userId;

  const handleStartThread = async () => {
    console.log("[UserProfilePane] Start Thread handler invoked", {
      userId: user.userId,
      myUserId,
      activeTab: useStore.getState().activeTab,
    });
    if (!user.userId || !myUserId) {
      console.warn("[UserProfilePane] cannot start thread, missing ids", { userId: user.userId, myUserId });
      return;
    }
    const threadKey = createDirectThreadKey(myUserId, user.userId);
    console.log("[UserProfilePane] Start Thread clicked", {
      myUserId,
      userId: user.userId,
      threadKey,
    });

    // Ensure a thread entry exists for the DM BEFORE switching tabs
    const store = useStore.getState();
    const existingThread = (store as any).threadMap?.[threadKey];
    if (!existingThread) {
      const threadName = user.fullHandle || user.username || user.userId;
      const newThread: Group = {
        id: threadKey,
        name: threadName,
        description: null,
        communityId: null,
        ownerId: null,
        groupKey: null,
        members: null, // members is for group chats with Member objects, not DMs
        membershipStatus: null,
        image: null,
        unread: 0,
        lastMessage: "",
        hiloMeta: null,
        isAnnouncementGroup: null,
        isDefaultGroup: null,
        isStandaloneGroup: null,
        isMute: null,
        isBlock: null,
        version: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      } as unknown as Group;
      console.log("[UserProfilePane] creating new thread entry", newThread);
      // Use the store action directly to ensure state update
      useStore.getState().setGroup(newThread);
      // Verify the thread was added
      const updatedThreadMap = useStore.getState().threadMap;
      console.log("[UserProfilePane] after setGroup, threadMap has:", {
        count: Object.keys(updatedThreadMap).length,
        hasNewThread: !!updatedThreadMap[threadKey],
        threadKeys: Object.keys(updatedThreadMap),
      });
      // Force thread list to re-render
      useStore.getState().incrementProfileRenderRef?.();
      try {
        await db.insertGrp(newThread);
      } catch (err) {
        console.warn("[UserProfilePane] failed to persist new thread", err);
      }
    }

    // Now switch to messages tab after thread is created
    setActiveTab?.("/threads-tab");

    // Eagerly select in the zustand store so UI updates immediately
    // Don't pass groupId for DMs - it should only be set for actual group chats
    selectThread?.({
      threadKey,
      receiverId: user.userId,
      senderId: myUserId,
      title: user.username,
    });

    // Best-effort local hydration from storage
    try {
      const messages = await db.getMessagesByThreadId(threadKey);
      setMessages?.(threadKey, messages || []);
      setHasMore?.(threadKey, false);
      console.log("[UserProfilePane] hydrated messages from storage", {
        threadKey,
        count: messages?.length || 0,
        ids: messages?.map((m: any) => m.id),
      });
    } catch (err) {
      console.warn("[UserProfilePane] failed to hydrate messages locally", err);
    }

    // Hydrate via thread service (ensures websocket events wired)
    try {
      await threadService.init();
    } catch (err) {
      console.warn("[UserProfilePane] threadService init failed", err);
    }
    console.log("[UserProfilePane] calling threadService.select", {
      threadKey,
      senderId: myUserId,
      receiverId: user.userId,
    });
    console.log("[UserProfilePane] invoking threadService.select");
    threadService.select({
      threadKey,
      senderId: myUserId,
      receiverId: user.userId,
    });

    // Compatibility selection
    selectThread?.({
      threadKey: threadKey,
      receiverId: user.userId,
      senderId: myUserId,
      title: user.username,
    });
  };

  const handleConfirmReport = async () => {
    if (!user) return;

    setIsSubmitting(true);
    try {
      await actions.reportUserNew({
        user,
        reason: reportDescription,
        reasonCategory: reportCategory,
      });

      // Close both dialogs
      setIsConfirmDialogOpen(false);
      setIsReportDialogOpen(false);

      // Reset form
      setReportDescription("");
      setReportCategory("other");

      // Show success toast
      toast.success("Report submitted successfully", {
        description: "Thank you for helping keep our community safe.",
      });
    } catch (error) {
      console.error("Failed to submit report:", error);
      toast.error("Failed to submit report", {
        description: "Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full overflow-auto space-y-4 p-4">
      <Card>
        <CardContent className="pt-6 text-center">
          <div className="mb-4 flex justify-center">
            <UserProfilePicture
              user={user}
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "40px",
              }}
            />
          </div>

          <h2 className="text-xl font-semibold">{displayName}</h2>

          <p className="text-sm text-muted-foreground font-mono mt-2">
            {user.deviceId}
          </p>
        </CardContent>
      </Card>

      {!isMyself && (
        <ListContainer>
          <div
            className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 border-b"
            onClick={handleStartThread}
          >
            <MessageCircle className="w-5 h-5 text-primary" />
            <span>Start Thread</span>
          </div>

        </ListContainer>
      )}

      {!isMyself && (
        <ListContainer>
          <div
            className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 border-b"
            onClick={() => user.isBlocked ? actions.unblockUserNew({ user }) : actions.blockUserNew({ user })}
          >
            <Ban className="w-5 h-5 text-orange-500" />
            <span>{user.isBlocked ? "Unblock User" : "Block User"}</span>
          </div>

          <div
            className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 border-b"
            onClick={() => setIsReportDialogOpen(true)}
          >
            <AlertTriangle className="w-5 h-5 text-destructive" />
            <span>Report User</span>
          </div>

          <div
            className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50"
            onClick={() => actions.deleteUser({ user })}
          >
            <Trash2 className="w-5 h-5 text-destructive" />
            <span>Delete User</span>
          </div>
        </ListContainer>
      )}

      {user.publicKeys?.some((key) => key.deviceId === user.deviceId) && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-primary" />
              <span className="text-sm text-primary">Verified User</span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          <h4 className="font-medium mb-3">User Details</h4>

          <div className="text-sm space-y-2">
            <div>
              <strong>User ID:</strong> {user.userId}
            </div>

            <div>
              <strong>Device ID:</strong> {user.deviceId}
            </div>

            {user.username && (
              <div>
                <strong>Handle:</strong> {user.fullHandle || user.username}
              </div>
            )}

            <div>
              <strong>Public Keys:</strong> {user.publicKeys?.length || 0}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isReportDialogOpen} onOpenChange={setIsReportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report User</DialogTitle>
            <DialogDescription>
              Please select a reason and provide details about why you're reporting this user.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="category">Reason Category</Label>
              <Select value={reportCategory} onValueChange={setReportCategory}>
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="harassment">Harassment</SelectItem>
                  <SelectItem value="spam">Spam</SelectItem>
                  <SelectItem value="inappropriate">Inappropriate Content</SelectItem>
                  <SelectItem value="impersonation">Impersonation</SelectItem>
                  <SelectItem value="violence">Violence or Threats</SelectItem>
                  <SelectItem value="hate">Hate Speech</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="Provide additional details about the issue..."
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsReportDialogOpen(false);
                setReportDescription("");
                setReportCategory("other");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setIsReportDialogOpen(false);
                setIsConfirmDialogOpen(true);
              }}
            >
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Report</DialogTitle>
            <DialogDescription>
              Are you sure you want to report this user? This action will be reviewed by our moderation team.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="space-y-2 text-sm">
              <div>
                <strong>Category:</strong>{" "}
                <span className="capitalize">{reportCategory.replace(/_/g, " ")}</span>
              </div>
              {reportDescription && (
                <div>
                  <strong>Description:</strong>
                  <p className="mt-1 text-muted-foreground">{reportDescription}</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsConfirmDialogOpen(false);
                setIsReportDialogOpen(true);
              }}
              disabled={isSubmitting}
            >
              Back
            </Button>
            <Button
              onClick={handleConfirmReport}
              disabled={isSubmitting}
              variant="destructive"
            >
              {isSubmitting ? "Submitting..." : "Confirm Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserProfilePane;
